/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useCallback, useEffect, useMemo } from 'react';

import { jsx } from '@compiled/react';
import { di } from 'react-magnetic-di';

import { addRovoParamsToUrl } from '@atlaskit/rovo-triggers/params';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { isChannelNotFoundError } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import {
	useUIStoreActions,
	useUIStoreSubscription,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { ChatConversationList } from '@atlassian/conversation-assistant-ui-components/ui/chat-conversation-list/ChatConversationList';

import { useAtlasURL } from '../../common/ui/hooks/use-atlas-url';
import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { isEmptyConversation } from '../../controllers/chat-store/isEmptyConversation';
import { useChatStore } from '../../controllers/chat-store/useChatStore';
import { useConversation } from '../../controllers/conversation/useConversation';

type ConversationListProps = {
	chatRefreshHeader?: React.ReactNode;
};

export const ConversationList = ({ chatRefreshHeader }: ConversationListProps): JSX.Element => {
	di(useGlobalConfig, useChatStore, useUIStoreActions, useUIConfig, useStagingAreaActions);
	useUIStoreSubscription();
	const { setActiveMenu } = useUIStoreActions();
	const { resetStagingArea } = useStagingAreaActions();
	const { fullScreen } = useUIConfig();
	const [
		{ conversations: conversationsStore, currentConversationId, conversationPagination },
		{
			setSelectedConversation,
			updateConversation,
			deleteConversation,
			loadMoreConversationChannelsData,
			setConversationListMounted,
		},
	] = useChatStore();
	const { closeChat } = useGlobalConfig();
	const { atlasURL } = useAtlasURL();
	const { conversation } = useConversation();
	const { sendAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'rovoChatConversationHistory',
		});
		// Should only fire on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setConversationListMounted(true);

		return () => {
			setConversationListMounted(false);
		};
	}, [setConversationListMounted]);

	const handleItemClick = useCallback(
		(conversationId: string) => {
			if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
				resetStagingArea();
			}
			setSelectedConversation({
				conversationId,
			});
			setActiveMenu('none');
		},
		[resetStagingArea, setActiveMenu, setSelectedConversation],
	);

	const handleNewChatClick = useCallback(() => {
		if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
			resetStagingArea();
		}
		setSelectedConversation({
			conversationId: EMPTY_CONVERSATION_ID,
		});
		if (!fullScreen) {
			setActiveMenu('none');
		}
	}, [resetStagingArea, setActiveMenu, setSelectedConversation, fullScreen]);

	const handleEditConfirm = useCallback(
		async (conversationId: string, newName: string) => {
			try {
				await updateConversation({
					conversationId,
					data: {
						name: newName.trim(),
					},
				});
			} catch (error) {
				if (error instanceof ApiError && isChannelNotFoundError(error)) {
					return {
						error: error.message,
					};
				}

				throw error;
			}
		},
		[updateConversation],
	);

	const handleDeleteConfirm = useCallback(
		async (conversationId: string) => {
			try {
				await deleteConversation({
					conversationId,
				});
			} catch (error) {
				if (error instanceof ApiError && isChannelNotFoundError(error)) {
					return {
						error: error.message,
					};
				}

				throw error;
			}
		},
		[deleteConversation],
	);

	const handleCloseChat = useCallback(() => {
		if (fullScreen) {
			setActiveMenu('none');
		} else {
			closeChat();
		}
	}, [fullScreen, closeChat, setActiveMenu]);

	const conversations = useMemo(
		() =>
			Object.values(conversationsStore).map((conversation) => ({
				...conversation,
				isEmpty: isEmptyConversation(conversation.id),
			})),
		[conversationsStore],
	);

	return (
		<ChatConversationList
			conversations={conversations}
			isFullScreen={!!fullScreen}
			currentConversationId={currentConversationId}
			onSelectConversation={handleItemClick}
			onNewConversation={handleNewChatClick}
			onCloseChat={handleCloseChat}
			onMaximizeChat={() => {
				window.open(
					addRovoParamsToUrl(atlasURL('/chat'), {
						pathway: 'chat',
						conversationId: !isEmptyConversation(conversation?.id) ? conversation?.id : undefined,
					}),
					'_blank',
					'noopener, noreferrer',
				);
			}}
			onConfirmEditConversation={handleEditConfirm}
			onConfirmDeleteConversation={handleDeleteConfirm}
			chatRefreshHeader={chatRefreshHeader}
			hasMoreConversations={conversationPagination.hasMore}
			isLoadingMoreConversations={conversationPagination.isLoadingMore}
			hasLoadMoreConversationsError={Boolean(conversationPagination.loadMoreError)}
			onLoadMoreConversations={loadMoreConversationChannelsData}
		/>
	);
};
